import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { withTranslation } from '../../ChildrenContext.js';
import PropTypes from 'prop-types';
import DropdownList from 'react-widgets/DropdownList';
import DatePicker from '../../DataComponent/DataComponent__DatePicker';
import moment from 'moment';
import config from '../../../config';
import TZ from '../../../utils/Timezone/Timezone';
import Switch from '../../Switch';

export const StatisticsFilter = ({ startDate, endDate, timezone, bindActions, groupBy, count, onlyForTrafficInPeriod, onAction, t }) => {
    const normalizeCount = useCallback((value) => {
        return (value === undefined || value === null || value === '') ? value : Number(value);
    }, []);
    const [state, setState] = useState({ startDate, endDate, timezone, bindActions, groupBy, count: normalizeCount(count), onlyForTrafficInPeriod: onlyForTrafficInPeriod ? 1 : 0 });
    const prevStateRef = useRef(null);

    useEffect(() => {
        const next = { ...state, startDate, endDate, timezone, bindActions, groupBy, count: normalizeCount(count), onlyForTrafficInPeriod: onlyForTrafficInPeriod ? 1 : 0 };
        const diffs = ['startDate','endDate','timezone','bindActions','groupBy','count','onlyForTrafficInPeriod'].some((k) => {
            const a = state[k];
            const b = next[k];
            if (k === 'startDate' || k === 'endDate') {
                const av = a && a.valueOf ? a.valueOf() : a;
                const bv = b && b.valueOf ? b.valueOf() : b;
                return av !== bv;
            }
            return a !== b;
        });
        if (diffs) setState(next);
    }, [startDate, endDate, timezone, bindActions, groupBy, count, onlyForTrafficInPeriod, setState]);

    const onGroupParamChange = useCallback((value) => {
        if (value && value.value) {
            setState(prev => ({ ...prev, groupBy: value.value }));
        }
    }, []);

    const onCountParamChange = useCallback((value) => {
        setState(prev => ({ ...prev, count: normalizeCount(value.value) }));
    }, [normalizeCount, setState]);

    const onBindActionsChange = useCallback((item) => {
        setState(prev => ({ ...prev, bindActions: item.value }));
    }, [setState]);

    const onOnlyForTrafficInPeriodChange = useCallback((checked) => {
        setState(prev => ({ ...prev, onlyForTrafficInPeriod: checked ? 1 : 0 }));
    }, [setState]);

    const onTimezoneChange = useCallback((item) => {
        setState(prev => ({ ...prev, timezone: item.value }));
    }, [setState]);

    const onDataPickerApply = useCallback((datapicker) => {
        const normalizedStart = datapicker.startDate ? moment(datapicker.startDate).startOf('day') : null;
        const normalizedEnd = datapicker.endDate ? moment(datapicker.endDate).endOf('day') : null;

        setState(prev => ({
            ...prev,
            startDate: normalizedStart,
            endDate: normalizedEnd
        }));
    }, [setState]);

    useEffect(() => {
        const prev = prevStateRef.current;
        const keys = ['startDate','endDate','bindActions','groupBy','count','timezone','onlyForTrafficInPeriod'];
        const changed = !prev || keys.some((k) => {
            const a = state[k];
            const b = prev[k];
            if (k === 'startDate' || k === 'endDate') {
                const av = a && a.valueOf ? a.valueOf() : a;
                const bv = b && b.valueOf ? b.valueOf() : b;
                return av !== bv;
            }
            return a !== b;
        });

        if (changed) {
            prevStateRef.current = state;
            onAction && onAction(state);
        }
    }, [state, onAction]);

    const staticNames = (t && t.reportPage) || {};
    const groupParams = useMemo(() => {
        const params = (t && t.statistics && t.statistics.groupParams) || [];
        return Object.keys(params).map((key) => ({ title: params[key], value: key }));
    }, [t]);
    const countParams = useMemo(() => {
        const params = Array.isArray(config.listCounts) && config.listCounts.length ? config.listCounts : [10,20,50,100];
        return params.map((count) => ({ title: count, value: count }));
    }, []);

    const timezoneOptions = useMemo(() => {
        return (TZ.getAllTimezones() || []).map(tz => ({
            value: tz.key,
            title: tz.nameLong || tz.name || tz.key
        }));
    }, []);

    return (
        <div className="statistics__top form-inline form-horizontal">
            <div className="row table-filter">
                <div className="table-filter__item">
                    <label>{staticNames.date}:</label>
                    <DatePicker
                        onApply={onDataPickerApply}
                        startDate={state.startDate}
                        endDate={state.endDate}
                        className="btn form-control reactdaypicker-block"
                    />
                </div>
                <div className="table-filter__item">
                    <label>{t?.timezone?.title}:</label>
                    <DropdownList
                        className={'rw-dropdownlist-select'}
                        filter='contains'
                        dataKey="value"
                        textField='title'
                        value={state.timezone}
                        data={timezoneOptions}
                        onChange={onTimezoneChange}
                    />
                </div>
                <div className="table-filter__item table-filter__item2x">
                    <label>{staticNames.onlyForTrafficInPeriod || 'Only for traffic of the selected period'}:</label>
                    <div className="table-filter__switch">
                        <Switch
                            checked={Boolean(state.onlyForTrafficInPeriod)}
                            onChange={onOnlyForTrafficInPeriodChange}
                            onText={t?.actions?.yes || 'Yes'}
                            offText={t?.actions?.no || 'No'}
                        />
                    </div>
                </div>
            </div>
            <div className="row table-filter">
                <div className="table-filter__item">
                    <label>{staticNames.groupBy}:</label>
                    <DropdownList
                        className={'rw-dropdownlist-select'}
                        filter='startsWith'
                        valueField='value'
                        textField='title'
                        value={groupParams.find(p => p.value === state.groupBy) || null}
                        data={groupParams}
                        onChange={onGroupParamChange}
                    />
                </div>
                <div className="table-filter__item table-filter__item2x">
                    <label>{staticNames.connectActions}:</label>
                    <DropdownList
                        className={'rw-dropdownlist-select'}
                        dataKey="value"
                        textField='title'
                        value={state.bindActions}
                        data={[
                            { title: staticNames.withTime, value: 'time' },
                            { title: staticNames.withTraffic, value: 'traffic' }
                        ]}
                        onChange={onBindActionsChange}
                    />
                </div>
                <div className="table-filter__item">
                    <label>{staticNames.count}:</label>
                    <DropdownList
                        className={'rw-dropdownlist-select'}
                        filter='startsWith'
                        valueField="value"
                        textField='title'
                        value={state.count}
                        data={countParams}
                        onChange={onCountParamChange}
                    />
                </div>
            </div>
        </div>
    );
};

StatisticsFilter.propTypes = {
    t: PropTypes.object,
    startDate: PropTypes.any,
    endDate: PropTypes.any,
    bindActions: PropTypes.string,
    timezone: PropTypes.string,
    groupBy: PropTypes.string,
    count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onlyForTrafficInPeriod: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    onAction: PropTypes.func
};

export default withTranslation(StatisticsFilter);
